import { z } from "zod";
import { ParseResult, ok, err } from "./result";

/** Content-pack format version this engine understands. See CONTENT-PACK-SPEC.md. */
export const SPEC_VERSION = 1;

/** Lowercase, dash-separated slug (e.g. "preparing-for-marriage"). */
const slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase slug (a-z, 0-9, dashes)");

const semver = z
  .string()
  .regex(/^\d+\.\d+\.\d+$/, "must be a semver version (e.g. 1.0.0)");

/** A loose BCP-47-ish language tag (e.g. "en", "es", "pt-BR"). */
const langTag = z
  .string()
  .regex(/^[a-z]{2}(?:-[A-Z]{2})?$/, "must be a language tag (e.g. en, es, pt-BR)");

/**
 * Module types the shell ships renderers for. Packs may only use these.
 * New types are added to the shell, never downloaded as code.
 */
export const MODULE_TYPES = [
  "reading",
  "checklist",
  "quiz",
  "assessment",
  "audio",
  "flashcards",
  "scripture",
  "video",
  "lesson",
  "glossary",
  // A calendar/schedule/roster lesson (docs/087-EVENTS-CALENDAR-MODULE.md v1). Today's
  // materials build still emits this as a `reading` lesson + `<stem>.events.json`
  // sidecar (detected at the app layer, see `app/lesson.tsx`); this explicit type is
  // the future-clean path a later materials build can switch to once every shipped
  // app understands it — adding it to the enum now is safe/additive (widens what
  // parses; doesn't change how `reading` lessons behave).
  "events",
  // A tagged, front-only card list — filter by tag, shuffle, "pick one for me"
  // (docs/153-TAGGED-CARD-DECKS.md). Deliberately a SIBLING of `flashcards`, not an
  // extension: `flashcards`' `back` is required (Q→A + SRS grading), so a front-only
  // card there is a hard parse error on any app build that doesn't have this
  // widened schema — whereas a brand-new type degrades cleanly (see
  // docs/108-BACKWARD-COMPATIBILITY.md's placeholder) on an older app.
  "deck",
  // A single-topic structured reference card — a summary, an ordered set of
  // flexibly-labeled sections, optional scripture passages, "see also" cross-
  // references, and tags (docs/176-REFERENCE-AND-TIMER-MODULES.md Part A).
  "reference",
  // A live, ticking time display — countdown/to-time/clock/elapsed — the
  // ProPresenter present/kiosk family (docs/176-REFERENCE-AND-TIMER-MODULES.md
  // Part B). The JSON is display CONFIG; the app computes the live value.
  "timer",
  // A filterable, browsable list of items tagged across one or more named facets,
  // each rendered as a chip-filter group (docs/175-FILTERABLE-LIST-AND-NEW-CONTENT-
  // TYPES.md v0). Generalizes `assessment`'s single category axis / `deck`'s single
  // free-form tag axis into N named facets — a first-class type (not a sidecar,
  // unlike `events`/`feed`) since a list has no meaningful plain-reading fallback.
  "list",
  // An ordered, one-step-at-a-time guided walkthrough (Next/Back) — docs/
  // 203-WIZARD-MODULE.md. Steps carry optional `choices` for branching
  // (decision-tree). A first-class type (no plain-reading fallback) — the JSON is
  // structured step data (id/title/body/choices), not prose.
  "wizard",
  // A lesson that IS a remote resource — a YouTube/Vimeo video, an article, a PDF,
  // any URL (docs/212-LINK-CONTENT-TYPE-AND-EXTERNAL-LINK-HANDLING.md v0). Unlike
  // every other JSON-file-backed type above, its data lives directly on the lesson
  // object (`url`/`openMode`/`showUrl`/`source` below), the same shape `media`
  // already uses for audio/video. Default embeds a recognized video host inline
  // (`LinkModule` delegates to `VideoModule`); anything else opens externally via
  // `Linking.openURL`. A first-class type (no plain-reading fallback) — the lesson
  // body IS the link, not prose.
  "link",
] as const;
export const ModuleTypeSchema = z.enum(MODULE_TYPES);
export type ModuleType = z.infer<typeof ModuleTypeSchema>;

const KNOWN_MODULE_TYPES: ReadonlySet<string> = new Set(MODULE_TYPES);

/**
 * A parsed lesson's `type`: a known {@link ModuleType}, or the forward-compatible
 * fallback `"unknown"` (docs/108-BACKWARD-COMPATIBILITY.md v0). `"unknown"` is
 * deliberately NOT added to `MODULE_TYPES`/`ModuleTypeSchema` — it's a fallback SHAPE
 * `parseManifest` produces for a lesson whose authored `type` this app build doesn't
 * recognize, never a value a pack author would write. See the `LessonSchema` preprocess
 * below and `COMPATIBILITY.md` for the ledger of what each type/flag does on an older app.
 */
export const LessonTypeSchema = z.union([ModuleTypeSchema, z.literal("unknown")]);
export type LessonType = z.infer<typeof LessonTypeSchema>;

/** Git-derived change date, ISO `YYYY-MM-DD` (see ministry-training-materials/gitmeta). */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be an ISO date (YYYY-MM-DD)");

/**
 * Capability flags the content build stamps on packs/lessons (catalog `1.2.0`, pack
 * `1.3.0` — see docs/038-CONTENT-CAPABILITY-FLAGS.md). Most flags — including `print` —
 * default **on**, so content that never sets `features` gets every capability (the app
 * ships with all features enabled by default; membership gating is deferred, see
 * docs/081-MEMBERSHIP-REENABLE.md). Content can still opt a capability off per pack/lesson.
 * The one exception is `remind`, which defaults **off** (opt-in).
 */
export interface Features {
  tts: boolean;
  print: boolean;
  present: boolean;
  bookmark: boolean;
  /** Show the next/previous lesson navigation buttons on a lesson. Default on. */
  nav: boolean;
  /**
   * Allow saving/copying a lesson's Markdown images to the device — the iOS long-press
   * "Save Image" WKWebView callout menu, plus a web right-click/drag guard
   * (docs/074-IMAGE-VIEWER-AND-SAVE.md). Default on (preserves today's behavior for
   * content that never sets `features`). A deterrent, not DRM: real protection is not
   * shipping the asset, or gating it server-side behind the content token.
   */
  saveImages: boolean;
  /**
   * The app MAY offer to set a reminder/notification for this item — the materials
   * "reminders" capability (materials plan 044; see docs/068-MATERIALS-REPO-SCHEMA-SYNC.md).
   * Defaults **off** (opt-in): the content build turns it on per item (via its
   * `reminders.json`). The app decides the reminder UX/cadence — this flag only signals
   * that reminders are *allowed*. Modeled here so the flag stops being discarded on parse;
   * the reminder-scheduling surface is a later follow-up (expo-notifications).
   */
  remind: boolean;
  /**
   * The app MAY offer a full-screen **TV / kiosk display mode** for this item — full-width
   * slides/presentation, optional nav buttons, optional auto-advance (materials plan 048;
   * see docs/068-MATERIALS-REPO-SCHEMA-SYNC.md and docs/091-KIOSK-DISPLAY-MODE.md). Defaults
   * **off** (opt-in): the content build turns it on per item (via its `kiosk.json`). Modeled
   * here so the flag stops being discarded on parse; the fullscreen kiosk view is the
   * app-side feature that consumes it.
   */
  kiosk: boolean;
}
export const DEFAULT_FEATURES: Features = {
  tts: true,
  print: true,
  present: true,
  bookmark: true,
  nav: true,
  saveImages: true,
  remind: false,
  kiosk: false,
};

const FeaturesSchema = z
  .object({
    tts: z.boolean().optional(),
    print: z.boolean().optional(),
    present: z.boolean().optional(),
    bookmark: z.boolean().optional(),
    nav: z.boolean().optional(),
    saveImages: z.boolean().optional(),
    remind: z.boolean().optional(),
    kiosk: z.boolean().optional(),
  })
  .optional();

/**
 * Resolve capability flags from most-specific to least, per key: each level (given
 * most-specific first, e.g. `resolveFeatures(lesson.features, pack.manifest.features)`)
 * may be a partial object; a key is taken from the first level that defines it, falling
 * back to `DEFAULT_FEATURES` when no level sets it.
 */
export function resolveFeatures(...levels: (Partial<Features> | undefined)[]): Features {
  const key = (k: keyof Features): boolean => {
    for (const level of levels) {
      const v = level?.[k];
      if (v !== undefined) return v;
    }
    return DEFAULT_FEATURES[k];
  };
  return {
    tts: key("tts"),
    print: key("print"),
    present: key("present"),
    bookmark: key("bookmark"),
    nav: key("nav"),
    saveImages: key("saveImages"),
    remind: key("remind"),
    kiosk: key("kiosk"),
  };
}

/**
 * Browse visibility a lesson/pack-entry/catalog-entry may declare (catalog `1.2.0`,
 * pack `1.3.0` — see docs/038-CONTENT-CAPABILITY-FLAGS.md §3). Emitted only when non-default
 * (`private`/`hidden`); `public` is the implicit default when omitted.
 */
export const VISIBILITY_VALUES = ["public", "private", "hidden"] as const;
export type Visibility = (typeof VISIBILITY_VALUES)[number];

const VisibilitySchema = z.enum(VISIBILITY_VALUES).optional();

/** Rank used by `resolveVisibility` — higher is more restrictive. */
const VISIBILITY_RANK: Record<Visibility, number> = { public: 0, private: 1, hidden: 2 };

/**
 * Optional presentation hint for `reading` lessons (docs/061-CONTENT-TYPE-TAXONOMY.md):
 * a label/icon-only variant — it never changes which renderer runs (`ReadingModule`
 * still renders). The "study guide, study notes, key points, devotional, summary,
 * cheat sheet, questions" requests are all this same `reading` lesson with a hint, not
 * a new module type. Closed set with a plain `"other"` fallback (see
 * `PresentationSchema` below) so a label the app doesn't recognize yet doesn't reject
 * the whole manifest.
 */
export const PRESENTATION_VALUES = [
  "guide",
  "notes",
  "keypoints",
  "devotional",
  "summary",
  "cheatsheet",
  "questions",
  // A `reading` authored as a paginated deck (split on `---` slide breaks) for present /
  // TV-kiosk mode (materials plan 048; docs/091-KIOSK-DISPLAY-MODE.md).
  "slides",
  "other",
] as const;
export type Presentation = (typeof PRESENTATION_VALUES)[number];

const PRESENTATION_SET: ReadonlySet<string> = new Set(PRESENTATION_VALUES);

/**
 * Lenient by design: any string parses (so a not-yet-supported label from newer
 * content never breaks the manifest); a value outside `PRESENTATION_VALUES` is
 * coerced to `"other"` rather than rejected. `undefined` (the field omitted) stays
 * `undefined` — today's plain-reading behavior.
 */
const PresentationSchema = z
  .string()
  .optional()
  .transform((v): Presentation | undefined => {
    if (v === undefined) return undefined;
    return PRESENTATION_SET.has(v) ? (v as Presentation) : "other";
  });

/**
 * i18n string-table key for a presentation hint's badge label (`presentation.<value>`
 * in `src/ui/strings.ts`), or `undefined` when there's no hint to show. Kept as a pure
 * mapping so the UI layer just calls `t(presentationLabelKey(lesson.presentation))`.
 */
export function presentationLabelKey(presentation: Presentation | undefined): string | undefined {
  return presentation ? `presentation.${presentation}` : undefined;
}

/**
 * Resolve `visibility` across levels (e.g. `resolveVisibility(lesson.visibility,
 * entry.visibility)`), taking the MOST RESTRICTIVE value seen at any level — unlike
 * `resolveFeatures` (most-specific/first-defined level wins per key), here `hidden`
 * beats `private` beats `public` regardless of which level set it. Defaults to
 * `public` when no level sets anything.
 */
export function resolveVisibility(...levels: (Visibility | undefined)[]): Visibility {
  let result: Visibility = "public";
  for (const level of levels) {
    if (level !== undefined && VISIBILITY_RANK[level] > VISIBILITY_RANK[result]) {
      result = level;
    }
  }
  return result;
}

/**
 * Opt-out platform availability map (materials plan 060; docs/207-PLATFORM-AVAILABILITY-
 * FILTER.md), a third, orthogonal axis from `Features`/`Visibility`. Absent ⇒ available
 * everywhere; a key explicitly set to `false` hides the item on that platform. Open key
 * set (`z.record(z.boolean())`, not a fixed enum like `features`) — an unrecognized key
 * (a platform token this app build doesn't know) is simply ignored, never a reason to
 * hide something, so future keys (`desktop`, `kiosk`, …) parse and degrade safely.
 */
export type PlatformMap = Record<string, boolean>;

const PlatformMapSchema = z.record(z.boolean()).optional();

/**
 * Resolve whether `current` platform is available given the opt-out maps seen at each
 * level (e.g. lesson, pack, catalog-entry, catalog-document). MOST-RESTRICTIVE-WINS: if
 * ANY provided level explicitly sets `current` to `false`, the item is unavailable.
 * Absent maps / absent keys default to available (today's behavior) — only `=== false`
 * ever hides; a stray `{ "web": true }` "opt-in" is a no-op, never re-enables something a
 * more-specific level turned off. Mirrors `resolveVisibility`'s "any level can only make
 * it more restrictive" shape (contrast `resolveFeatures`, where the most-specific level
 * wins per key). Unlike `resolveVisibility` (variadic values only), `current` is a
 * required first argument — the answer depends on which platform is asking. Pure;
 * coverage-gated.
 */
export function resolvePlatforms(current: string, ...levels: (PlatformMap | undefined)[]): boolean {
  for (const level of levels) {
    if (level && level[current] === false) return false;
  }
  return true;
}

/**
 * Resolve whether an item's media may be CAST to a TV (AirPlay/Chromecast,
 * docs/251-MEDIA-CASTING.md) given the `castable` flag seen at each level (lesson, pack
 * manifest, catalog entry, catalog document). Unlike `resolvePlatforms` (default ON, opt-out)
 * this is **opt-IN with a `false` default**: casting hands the receiver the REMOTE url even
 * for a downloaded item, re-incurring the provider's bandwidth, so nothing casts until a
 * level explicitly enables it. Rules, in order of precedence:
 *   - any level explicitly `false` → **false** (a catalog/pack can veto a lesson's opt-in);
 *   - else at least one level `true` → **true**;
 *   - else (nothing set it) → **false** (the default — no cast).
 * Pure; coverage-gated.
 */
export function resolveCastable(...levels: (boolean | undefined)[]): boolean {
  let anyTrue = false;
  for (const level of levels) {
    if (level === false) return false;
    if (level === true) anyTrue = true;
  }
  return anyTrue;
}

/**
 * One entry of a **node-level** `related` list (docs/242-RELATED-AND-SAMEAS-RENDERING.md
 * v0) — distinct from the existing **lesson-level** `related` (`LessonObjectSchema.related`,
 * a bare `string[]` of global lesson ids, docs/101-CONTENT-GRAPH.md). This is the
 * course/pack/catalog-node analogue: a sibling node id, authored either as a bare string
 * (title resolved locally from the loaded catalog tree) or as `{id, title}` (a carried
 * title, used defensively so a later cross-catalog case — an id this app instance can't
 * resolve locally — can still render a title; materials 285's own open decision #4).
 * Support for both forms is a parsing-time concession only; resolution/rendering lives in
 * `content/nodeRelated.ts`.
 */
export const NodeRelatedRefSchema = z.union([
  z.string(),
  z.object({ id: z.string(), title: z.string().optional() }),
]);
export type NodeRelatedRef = z.infer<typeof NodeRelatedRefSchema>;
export const NodeRelatedListSchema = z.array(NodeRelatedRefSchema).optional();

/**
 * Opt-in **opaque equality key** (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0): two catalog
 * pack/catalog-node entries carrying the SAME `sameAs` string are the same underlying
 * content published under different ids (materials 285's unique-subset-id case). Never
 * parsed/interpreted — grouped by plain string equality only (`content/sameAs.ts`
 * `sameAsGroups`). Never itself a progress/storage key — see `canonicalGroupId` there.
 */
export const SameAsSchema = z.string().optional();

/**
 * A section-splitter source (docs/231-SECTION-SPLITTER-MANIFEST-FIELD.md): either the
 * app-known `"headings"` kind (Option 1 — a closed, extensible enum; v0 ships just this
 * one value) or a literal-prefix marker (Option 2, the recommended/primary form for
 * content that marks structure without `#` headings, e.g. the SLTI textbooks'
 * `[[page: 14]]` lines). **Never a regex** — an imported zip is untrusted input
 * (docs/217-BINARY-AUDIO-IMPORT.md's zip-import threat model), so a manifest-declared
 * pattern that reached a regex engine would be a ReDoS vector; matching is always plain
 * `String.startsWith`/`endsWith` on the trimmed line, and every string field here is
 * length-capped so a single source can't itself be a resource-exhaustion vector.
 */
const SectionSourceSchema = z.union([
  z.enum(["headings"]),
  z
    .object({
      /** A trimmed body line that `startsWith` this opens a new section. */
      linePrefix: z.string().min(1).max(40),
      /** When given, the line must also `endsWith` this; the token is the literal text
       * between prefix and suffix (else: everything after the prefix), trimmed. */
      suffix: z.string().max(40).optional(),
      /** A label combined with the matched token, e.g. `"Page"` + `"14"` → "Page 14". */
      title: z.string().max(40).optional(),
    })
    .strict(),
]);
export type SectionSource = z.infer<typeof SectionSourceSchema>;

/**
 * Pack-level, optional (docs/231-SECTION-SPLITTER-MANIFEST-FIELD.md): declares what
 * splits a `reading` lesson's body into sections, in place of (or alongside) `#`
 * headings — applies to every lesson in the pack, no per-lesson override in v0.
 * `from` is ordered; a line is claimed by the FIRST source that matches it, so
 * `"headings"` and a marker can coexist. Bounded to at most 8 sources (`.min(1)` so an
 * explicit empty list is rejected rather than silently meaning "no sections at all").
 * Absent ⇒ `{ from: ["headings"] }`, today's headings-only behavior, unchanged.
 */
export const SectionsSchema = z
  .object({
    from: z.array(SectionSourceSchema).min(1).max(8),
  })
  .strict();
export type Sections = z.infer<typeof SectionsSchema>;

/**
 * Preprocess step that makes an unrecognized lesson `type` forward-compatible
 * (docs/108-BACKWARD-COMPATIBILITY.md v0): rather than the whole pack failing a Zod
 * enum check, a lesson whose `type` isn't in `MODULE_TYPES` is rewritten to
 * `type: "unknown"` with the ENTIRE original lesson object preserved on `raw` (so a
 * future app build that does understand it — or a human debugging — still has the
 * original data; this build only shows an "update the app" placeholder for it). Known
 * types pass through untouched. Non-object/typeless input is left alone so the normal
 * schema error messages still fire (missing `type` etc.).
 */
function normalizeUnknownLessonType(input: unknown): unknown {
  if (input && typeof input === "object" && "type" in input) {
    const t = (input as { type: unknown }).type;
    if (typeof t === "string" && !KNOWN_MODULE_TYPES.has(t)) {
      return { ...(input as Record<string, unknown>), type: "unknown", raw: input };
    }
  }
  return input;
}

/**
 * One narration audio TRACK for a `reading` lesson — multiple narrators/voices/
 * languages per lesson (materials plan 358; docs/338-CONSUME-NARRATION-CAPTIONS-AND-
 * NARRATIONS.md). `@equipped/core/modules/media/narrationTracks.ts`'s `NarrationTrack`
 * mirrors this shape structurally as an independent type (this file's `modules/media/*`
 * files never import `core/manifest` directly — see that file's own doc), so a parsed
 * `Lesson.narrations` array is usable there with no cast.
 */
const NarrationTrackSchema = z.object({
  /** Pack-relative path (or absolute URL) to this track's audio file. Required — a
   *  track with no audio is not a track. */
  audio: z.string().min(1, "audio is required"),
  /** Pack-relative path to a WebVTT closed-caption/timed-text track for THIS track's
   *  audio (own file per narrator/language — distinct from the other tracks' own). */
  captions: z.string().optional(),
  /** Pack-relative path to a richer read-along timing sidecar for THIS track — app-
   *  only, PARSED but not yet consumed (docs/316-NARRATION-SYNC-READALONG.md v1). */
  readalong: z.string().optional(),
  /** Human narrator/voice label shown in the track picker (e.g. "Mónica", "Paulina"). */
  narrator: z.string().optional(),
  /** The language this track's audio is spoken in (distinct from the lesson's own
   *  reading-content language) — used to pick a sensible default track. */
  lang: langTag.optional(),
  duration: z.number().nonnegative().optional(),
});

const LessonObjectSchema = z.object({
  id: slug,
  title: z.string().min(1, "title is required"),
  type: LessonTypeSchema,
  file: z.string().min(1, "file is required"),
  optional: z.boolean().optional().default(false),
  // Optional media source for the audio/video modules (pack-relative path or URL).
  media: z.string().optional(),
  duration: z.number().nonnegative().optional(),
  // Optional pre-generated narration audio (TTS) for reading lessons.
  narration: z.string().optional(),
  // A pack-relative WebVTT closed-caption/timed-text track for this lesson's
  // narration/media audio (materials plan 358; docs/338-CONSUME-NARRATION-CAPTIONS-AND-
  // NARRATIONS.md). Applies to whichever audio actually plays — the single `narration`
  // track above, or (when `narrations` below is present) MIRRORS its PRIMARY (first)
  // track's own `captions`, so an app build that predates `narrations` still shows the
  // right caption file.
  captions: z.string().optional(),
  // A pack-relative richer read-along timing sidecar (sentence/word timings + reader-
  // block anchors) — app-only, PARSED but not yet consumed by this build (docs/
  // 316-NARRATION-SYNC-READALONG.md v1 / docs/338). Mirrors `narrations[0].readalong`
  // for the same back-compat reason as `captions` above.
  readalong: z.string().optional(),
  // MULTIPLE narration audio tracks for this lesson — e.g. several TTS voices/human
  // narrators, optionally in different languages (materials plan 358; docs/
  // 338-CONSUME-NARRATION-CAPTIONS-AND-NARRATIONS.md). When present, the flat
  // `narration`/`captions` fields above MIRROR the PRIMARY (first) item for an app
  // build that predates this field; a current build prefers `narrations` (it carries
  // the full per-track `narrator`/`lang`/`duration` metadata the flat fields don't) —
  // see `@equipped/core/modules/media/narrationTracks`'s `resolveNarrationTracks`.
  narrations: z.array(NarrationTrackSchema).optional(),
  // A muted secondary line shown under the lesson title on the pack's lesson rows (e.g.
  // "from Session 1 (Introduction)"). Pre-localized by the content build.
  subtitle: z.string().optional(),
  // Plain-text blurb about this lesson, shown via the ⓘ details button in the reader
  // header (docs/047-CONTENT-DESCRIPTIONS.md). No cross-level resolution.
  description: z.string().optional(),
  // The languages THIS lesson ships a translated `<base>.<lang>.<ext>` sibling for
  // (including the default). Optional and additive: when the content build populates it,
  // the reader's translate control reads it directly instead of probing the source for
  // each sibling; when absent, availability falls back to a runtime probe. Pack-level
  // `languages` (below) stays the advisory pack-wide set.
  languages: z.array(langTag).nonempty().optional(),
  // Change-tracking, git-derived by the content build (both optional): the lesson's
  // newest change date (counting translation siblings) and a content fingerprint.
  updated: isoDate.optional(),
  contentHash: z.string().optional(),
  // Content graph v0 (docs/101-CONTENT-GRAPH.md, materials plan 052): free-form tag
  // ids. Language-neutral, app-humanized for display in v0 (no `tag-labels.<lang>.json`
  // consumer yet) — display-only chips (tappable topic browse is v1). Optional/
  // additive: a lesson without them renders exactly as before (docs/108).
  tags: z.array(z.string()).optional(),
  // Content graph v0 (docs/101-CONTENT-GRAPH.md, materials plan 052): GLOBAL lesson
  // ids this lesson is related to — NOT the pack-local lesson slug. Resolved to
  // openable refs by `content/relatedContent.ts` (`indexLessonsById`/`relatedFor`); an
  // id that doesn't resolve (not found in any pack loaded this session) is skipped,
  // never a dangling UI entry. Optional/additive, same back-compat contract as `tags`.
  related: z.array(z.string()).optional(),
  // Per-key capability flags (docs/038-CONTENT-CAPABILITY-FLAGS.md); resolved against the
  // pack (and DEFAULT_FEATURES) via `resolveFeatures`. Optional/partial so hand-authored
  // lessons still parse.
  features: FeaturesSchema,
  // Browse visibility (docs/038-CONTENT-CAPABILITY-FLAGS.md §3); resolved most-restrictive-
  // wins via `resolveVisibility`. Omitted = public. `private` has no dedicated "private
  // section" UI yet, so v0 treats it the same as `hidden` when filtering browse lists
  // (see `visibleLessons` below and `content/catalogTree.ts`).
  visibility: VisibilitySchema,
  // Platform availability opt-out (materials plan 060; docs/207-PLATFORM-AVAILABILITY-
  // FILTER.md); resolved most-restrictive-wins via `resolvePlatforms` against the pack
  // manifest's own `platforms` (below). Omitted = available on every platform.
  platforms: PlatformMapSchema,
  // Whether this lesson's media may be cast to a TV (docs/251-MEDIA-CASTING.md). Default OFF;
  // resolved with the pack + catalog levels via `resolveCastable` (any false vetoes).
  castable: z.boolean().optional(),
  // Optional label/icon hint for a `reading` lesson (docs/061-CONTENT-TYPE-TAXONOMY.md).
  // Does not change which renderer runs; v0 drives only a type badge on the lesson row.
  presentation: PresentationSchema,
  // `link` lesson fields (docs/212-LINK-CONTENT-TYPE-AND-EXTERNAL-LINK-HANDLING.md v0).
  // Optional at the schema level (like `media` above) since every lesson field is
  // shared across types — a `link` lesson missing `url` renders `ModuleRenderer`'s
  // "missing data" placeholder rather than failing to parse.
  //
  // The link's target URL.
  url: z.string().url().optional(),
  // How the URL renders: "embed" plays/shows it inline, "external" always opens it
  // via `Linking.openURL`, "auto" (the default when omitted) resolves via
  // `toEmbed(url)` (a recognized video host) + the user's global "Open web content"
  // setting — see `resolveLinkDisplay` (`@equipped/core/modules/link/resolveDisplay`).
  openMode: z.enum(["embed", "external", "auto"]).optional(),
  // Show the resolved URL under the title (transparency) vs. just a titled button/
  // card. `undefined` behaves like `false`.
  showUrl: z.boolean().optional(),
  // Icon/label hint only — the app always derives actual embeddability itself via
  // `toEmbed(url)`; this field never changes rendering logic.
  source: z.enum(["youtube", "vimeo", "web"]).optional(),
  // Present only when `type === "unknown"` — the original, unrecognized lesson object
  // as authored (docs/108-BACKWARD-COMPATIBILITY.md v0), populated by
  // `normalizeUnknownLessonType` below. Not authored directly by content.
  raw: z.unknown().optional(),
});

export const LessonSchema = z.preprocess(normalizeUnknownLessonType, LessonObjectSchema);
export type Lesson = z.infer<typeof LessonSchema>;

export const ManifestSchema = z
  .object({
    specVersion: z.literal(SPEC_VERSION),
    id: slug,
    title: z.string().min(1, "title is required"),
    subtitle: z.string().optional(),
    // Plain-text blurb about this pack, shown via the ⓘ details button (docs/
    // CONTENT-DESCRIPTIONS.md). No cross-level resolution.
    description: z.string().optional(),
    author: z.string().optional(),
    version: semver,
    // Pack-level change tracking (git-derived, optional): newest lesson change date and a
    // rolled-up content fingerprint used for cache invalidation. `version` is a CalVer
    // (YYYY.MM.DD) that also satisfies the semver pattern.
    updated: isoDate.optional(),
    contentHash: z.string().optional(),
    icon: z.string().optional(),
    tags: z.array(z.string()).optional().default([]),
    access: z.enum(["free", "paid"]).optional().default("free"),
    // Whether this pack's files may be downloaded for offline use
    // (docs/051-DOWNLOADS-OFFLINE.md). Default true when absent (the common case).
    // Resolved against the catalog entry's own `CatalogPackEntry.downloadable` hint via
    // `canDownload` (content/download.ts) — either level saying false wins.
    downloadable: z.boolean().optional(),
    // Whether this pack's audio/video/narration media may be CAST (AirPlay/Chromecast) to a
    // TV (docs/251-MEDIA-CASTING.md). Default OFF (absent = not castable) — casting hands the
    // receiver the REMOTE url even for a downloaded item, re-incurring provider bandwidth, so
    // it must be opted in. Resolved most-restrictive-wins with a lesson's own `castable` +
    // the catalog levels via `resolveCastable` (any level saying false vetoes).
    castable: z.boolean().optional(),
    // TV-media hint (docs/322-ROKU-FEED-COVERAGE.md v0 Part 3) — a NEGATIVE-skip
    // opt-out: `false` means "this pack is known media-less, don't fetch its manifest
    // when walking for a TV feed"; absent/`true` = walk it (today's behavior, fully
    // back-compat). Mirrors `castable`'s placement one field up but the opposite
    // default polarity — this hint exists purely to bound a live Roku-feed walk's
    // fetch budget, not to gate playback, so "unknown" must stay "walk it," never
    // "skip it." v0 is consumed ONLY by the Worker's duck-typed `rokuFeed.ts` (no
    // `resolveCastable`-style app-side resolver here — app-side resolution deferred).
    tvMedia: z.boolean().optional(),
    // Languages this pack ships (build-for-English; translated lesson files follow
    // the `<base>.<lang>.<ext>` convention — see src/core/i18n.ts). Defaults to en.
    languages: z.array(langTag).nonempty().optional(),
    defaultLanguage: langTag.optional(),
    // Pack-level capability-flag defaults (docs/038-CONTENT-CAPABILITY-FLAGS.md); a lesson's
    // own `features` (if any) wins per key over these, per key over `DEFAULT_FEATURES`.
    features: FeaturesSchema,
    // Pack-level platform availability opt-out (materials plan 060; docs/207-PLATFORM-
    // AVAILABILITY-FILTER.md) — a lesson's own `platforms` (if any) is resolved alongside
    // this via `resolvePlatforms` (most-restrictive-wins). Omitted = every platform.
    platforms: PlatformMapSchema,
    // Section-splitter declaration (docs/231-SECTION-SPLITTER-MANIFEST-FIELD.md) — see
    // `SectionsSchema`. Optional/additive; omitted = headings-only, today's behavior.
    sections: SectionsSchema.optional(),
    // Node-level "Related courses" see-also (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) —
    // the pack-manifest analogue of `CatalogPackEntry.related`/`CatalogNodeEntry.related`
    // (`content/nodeRelated.ts` resolves whichever source a screen has in hand). Distinct
    // from the existing LESSON-level `related` above (docs/101).
    related: NodeRelatedListSchema,
    // Opaque cross-placement identity (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) — see
    // `SameAsSchema`'s doc comment. Grouped by `content/sameAs.ts`.
    sameAs: SameAsSchema,
    lessons: z.array(LessonSchema).min(1, "a pack needs at least one lesson"),
  })
  .superRefine((m, ctx) => {
    const seen = new Set<string>();
    m.lessons.forEach((lesson, i) => {
      if (seen.has(lesson.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate lesson id: "${lesson.id}"`,
          path: ["lessons", i, "id"],
        });
      }
      seen.add(lesson.id);
    });
    if (m.defaultLanguage && m.languages && !m.languages.includes(m.defaultLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `defaultLanguage "${m.defaultLanguage}" is not in languages`,
        path: ["defaultLanguage"],
      });
    }
  });

export type Manifest = z.infer<typeof ManifestSchema>;

function formatIssue(issue: z.ZodIssue): string {
  const path = issue.path.join(".");
  return path ? `${path}: ${issue.message}` : issue.message;
}

/**
 * Validate an already-parsed object against the manifest schema. Unknown future
 * spec versions get a friendly "update the app" message instead of a raw error.
 */
export function parseManifest(input: unknown): ParseResult<Manifest> {
  if (input && typeof input === "object" && "specVersion" in input) {
    const sv = (input as { specVersion: unknown }).specVersion;
    if (typeof sv === "number" && sv > SPEC_VERSION) {
      return err([
        `This pack requires a newer app version (specVersion ${sv}; this app supports ${SPEC_VERSION}). Please update the app.`,
      ]);
    }
  }
  const result = ManifestSchema.safeParse(input);
  if (result.success) return ok(result.data);
  return err(result.error.issues.map(formatIssue));
}

/** Parse manifest JSON text, reporting malformed JSON as a friendly error. */
export function parseManifestJson(text: string): ParseResult<Manifest> {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return err(["manifest.json is not valid JSON"]);
  }
  return parseManifest(data);
}

/** Lessons that count toward completion (non-optional). */
export function requiredLessons(manifest: Manifest): Lesson[] {
  return manifest.lessons.filter((l) => !l.optional);
}

/**
 * Lessons shown in browse contexts (the pack's lesson list) — drops `hidden` and
 * `private` lessons (docs/038-CONTENT-CAPABILITY-FLAGS.md §3; v0 treats `private` like
 * `hidden` since there's no dedicated "private section" UI yet), and, when `current` is
 * given, lessons opted out of that platform (docs/207-PLATFORM-AVAILABILITY-FILTER.md) —
 * resolved most-restrictive-wins across the pack-manifest-level and lesson-level
 * `platforms` maps via `resolvePlatforms`. `current` omitted ⇒ no platform filtering
 * (today's behavior, kept for legacy/no-arg callers). Does not affect direct access by
 * id (deep links, bookmarks, recents) — only what's listed.
 */
export function visibleLessons(manifest: Manifest, current?: string): Lesson[] {
  return manifest.lessons.filter((l) => {
    if (resolveVisibility(l.visibility) !== "public") return false;
    if (current !== undefined && !resolvePlatforms(current, manifest.platforms, l.platforms)) return false;
    return true;
  });
}
